import React from 'react';

import { useIntl } from 'react-intl-next';

import { Box, Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { assert, AssertionError, isRequired, useValidators } from '@atlassian/eoc-common';
import { ErrorMessage, EocField as Field, useForm } from '@atlassian/eoc-form';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { type FormData } from '../../../../common/types';
import { useFormController } from '../../../../controllers/form';
import { useHeartbeatsContext } from '../../../../controllers/providers/heartbeats';
import { heartbeatsResource } from '../../../../services/resources';

import { AfterElements } from './input-after-elements';
import messages from './messages';
import { type Props } from './types';

const SpinnerStyles = xcss({
	position: 'absolute',
	right: `${token('space.negative.300', '-24px')}`,
});
const FlexStyles = xcss({
	flexGrow: 1,
	flexShrink: 1,
	flexBasis: 'auto',
	alignItems: 'center',
	justifyContent: 'center',
	position: 'relative',
});

export const NameInput: React.FC<Props> = ({ initialValue }) => {
	const { formatMessage } = useIntl();
	const { data } = useResource(heartbeatsResource);

	const { validateLoading, isValidateError, handleAsyncValidate } = useFormController();

	const { focusState } = useHeartbeatsContext();

	const { resumeValidation } = useForm();

	assert(
		!!data,
		new AssertionError('NameInput', 'NameInput component rendered before data is fetched'),
	);

	return (
		<Field<FormData['name']>
			aria-required={true}
			name="name"
			id="name"
			label={formatMessage(messages.name)}
			isRequired
			initialValue={initialValue}
			validate={useValidators([
				isRequired(formatMessage(messages.emptyNameErrorMessage)),
				handleAsyncValidate,
			])}
		>
			{({ input, meta }) => (
				<>
					<Flex xcss={FlexStyles}>
						<TextField
							{...input}
							isDisabled={focusState.mode === 'edit'}
							autoComplete="off"
							elemAfterInput={
								<AfterElements domain={data.heartbeatEmailDomain} currentValue={input.value} />
							}
							placeholder={formatMessage(messages.placeholder)}
							// This is for fixing the https://github.com/final-form/react-final-form/issues/676
							// It can be removed when final-form is upgraded to 4.20.2
							onBlur={() => {
								resumeValidation();
							}}
						/>
						{(validateLoading || meta.validating) && (
							<Box xcss={SpinnerStyles}>
								<Spinner size="small" />
							</Box>
						)}
					</Flex>
					{(meta.touched || meta.dirty) && isValidateError && meta.error && (
						<ErrorMessage>{meta.error}</ErrorMessage>
					)}
				</>
			)}
		</Field>
	);
};
